import { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MobileDetect from 'mobile-detect';
import screenfull from 'screenfull';
import { trans } from '@spotahome/soyuz/client';
import { Button } from '@spotahome/ui-library';

import MarzipanoConnector from '../../../../utils/MarzipanoConnector';

import './viewer-360.scss';

const PHOTO_VERSIONS = {
  mobile: 'small360',
  default: 'large360'
};

const Viewer360 = ({
  photos,
  onChangeScene = () => {},
  onLoad = () => {},
  onFullScreenOn = () => {},
  onFullScreenOff = () => {},
  isLandscape
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [currentScene, setCurrentScene] = useState(null);

  const wrapperRef = useRef(null);
  const marzipanoContainerRef = useRef(null);
  const marzipanoRef = useRef(null);

  const isMobile = useMemo(() => {
    if (typeof window !== 'undefined') {
      const md = new MobileDetect(window.navigator.userAgent);
      return md.phone();
    }
    return false;
  }, []);

  useEffect(() => {
    marzipanoRef.current = new MarzipanoConnector(photos, isMobile);
  }, [photos, isMobile]);

  // Inicialización automática al montar
  useEffect(() => {
    (async () => {
      try {
        await marzipanoRef.current.initialize(marzipanoContainerRef.current);
        setCurrentScene(photos[0].id);
        setIsLoaded(true);
      } catch (err) {
        throw err;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const elem = wrapperRef.current;
    if (screenfull.enabled) {
      if (isFullScreen) {
        screenfull.request(elem);
        screenfull.onchange(() => {
          screenfull.off('change');
        });
        onFullScreenOn();
      } else {
        screenfull.exit();
        screenfull.off('change');
        onFullScreenOff();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullScreen]);

  useEffect(() => {
    setTimeout(() => {
      if (
        marzipanoRef?.current?.viewer?.stage &&
        marzipanoContainerRef.current
      ) {
        const stage = marzipanoRef.current.viewer.stage();
        stage.setSize({
          height: marzipanoContainerRef.current.clientHeight,
          width: marzipanoContainerRef.current.clientWidth
        });
      }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullScreen, isLandscape]);

  useEffect(() => {
    if (isLoaded) {
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  useEffect(() => {
    if (currentScene) {
      const getCurrentSize = () =>
        isMobile ? PHOTO_VERSIONS.mobile : PHOTO_VERSIONS.default;
      marzipanoRef.current?.switchScene(currentScene, getCurrentSize());
      onChangeScene();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScene]);

  const handleFullScreenClick = () => {
    setIsFullScreen(prev => !prev);
  };

  const handleUpdateNavigation = event => {
    setCurrentScene(event.target.value);
  };

  const renderFullscreenControls = () => {
    return (
      <Button
        className="button__fullscreen"
        onClick={handleFullScreenClick}
        color="filled"
      >
        {isFullScreen ? (
          <span>
            {trans('property_show.viewer360.close')}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 13 11"
              width="15px"
              height="15px"
            >
              <path
                fill="none"
                stroke="#000"
                d="M1.367.367l9.906 9.906M12 0L2 10"
                strokeLinecap="square"
              />
            </svg>
          </span>
        ) : (
          <span>
            {trans('property_show.viewer360.fullscreen')}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 15 15"
              width="15px"
              height="15px"
            >
              <g fill="none" stroke="#000">
                <path d="M5 14H1v-4M1 5V1h4M10 1h4v4M14 10v4h-4" />
              </g>
            </svg>
          </span>
        )}
      </Button>
    );
  };

  const renderNavigationControls = () => {
    return (
      <span className="viewer-360__navigation">
        <select onChange={handleUpdateNavigation} value={currentScene || ''}>
          {photos.map(photo => (
            <option key={photo.id} value={photo.id}>
              {photo.caption}
            </option>
          ))}
        </select>
      </span>
    );
  };

  const viewerClassName = classNames('viewer-360', {
    'viewer-360--fullscreen': isFullScreen
  });

  const wrapperClassName = classNames('viewer-360__wrapper', {
    'viewer-360__wrapper--active': isLoaded
  });

  return (
    <Fragment>
      <div className={viewerClassName} ref={wrapperRef} id="viewer-360">
        {isLoaded && renderFullscreenControls()}
        {isLoaded && photos.length > 1 && renderNavigationControls()}
        <div className={wrapperClassName}>
          <div ref={marzipanoContainerRef} className="pano" />
        </div>
      </div>
    </Fragment>
  );
};

Viewer360.propTypes = {
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      caption: PropTypes.string,
      src: PropTypes.shape({
        small360: PropTypes.string,
        large360: PropTypes.string
      })
    })
  ).isRequired,
  onChangeScene: PropTypes.func,
  onLoad: PropTypes.func,
  onFullScreenOn: PropTypes.func,
  onFullScreenOff: PropTypes.func,
  isLandscape: PropTypes.bool.isRequired
};

export default Viewer360;
